<template>
  <div class="flow-summary">
    <!-- Current flow first, with its trend arrow; the range and the update time stay discreet -->
    <div v-if="!section._formattedCurrentLevel" class="flow-summary__empty">Aucune donnée</div>
    <div v-else class="flow-summary__level">
      <span :class="['flow-summary__value', section._levelColorClass]">{{ toFrench(section._formattedCurrentLevel) }}</span>
      <v-icon v-if="section._currentTrendSign === 1" size="small" color="green-darken-2" aria-hidden="true">
        mdi-arrow-up
      </v-icon>
      <v-icon v-else-if="section._currentTrendSign === -1" size="small" color="red" aria-hidden="true">
        mdi-arrow-down
      </v-icon>
    </div>
    <div v-if="hasTrend" :class="['flow-summary__trend', trendColorClass]" title="Variation">
      {{ trendText }} <acronym-tooltip code="mch"></acronym-tooltip>
    </div>
    <div v-if="hasRange" class="flow-summary__range" title="Plage recommandée">
      {{ formatFlow(section.min_sug_level) }}–{{ formatFlow(section.max_sug_level) }} mcs
    </div>
    <div v-if="section._lastUpdate" class="flow-summary__meta">
      <v-icon size="x-small">mdi-clock-time-three-outline</v-icon>
      {{ section._lastUpdate }}
    </div>
    <v-icon
      v-else-if="section.current_trend > 0"
      color="yellow-darken-3"
      size="small"
      :title="'Dernière mise à jour le ' + section._lastUpdateDayMonth"
    >
      mdi-alert
    </v-icon>
  </div>
</template>

<script>
  import AcronymTooltip from "@/components/AcronymTooltip.vue";
  import { StringService } from "@/services/String.service";

  // Flow column of the section tables, from the display fields built by the table (_formattedCurrentLevel...).
  export default {
    name: "SectionFlowSummary",
    components: { AcronymTooltip },
    props: {
      section: {
        type: Object,
        required: true,
      },
    },
    computed: {
      hasRange() {
        return Number(this.section.min_sug_level) > 0 && Number(this.section.max_sug_level) > 0;
      },
      hasTrend() {
        return this.section._currentTrendDisplay !== "" && this.section._currentTrendDisplay !== undefined;
      },
      // Signed variation, e.g. "+0.13" or "−0.03" ("0" when stable)
      trendText() {
        const sign = { 1: "+", "-1": "−" }[this.section._currentTrendSign] || "";
        return `${sign}${this.toFrench(this.section._currentTrendDisplay)}`;
      },
      trendColorClass() {
        return { 1: "text-green-darken-2", "-1": "text-red" }[this.section._currentTrendSign] || "text-medium-emphasis";
      },
    },
    methods: {
      formatFlow(value) {
        return this.toFrench(StringService.numberFormatter(value, 2));
      },
      // Decimal comma for display ("12,01", "6,2k"); the data keeps its dot
      toFrench(value) {
        return String(value ?? "").replace(".", ",");
      },
    },
  };
</script>

<style scoped>
  .flow-summary__level {
    display: inline-flex;
    align-items: center;
    gap: 2px;
  }
  .flow-summary__value {
    font-size: 1.05rem;
    font-weight: 700;
    line-height: 1.3;
  }
  .flow-summary__empty {
    font-size: 0.8rem;
    font-style: italic;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  }
  .flow-summary__trend {
    font-size: 0.8rem;
    font-weight: 600;
    white-space: nowrap;
  }
  .flow-summary__range {
    font-size: 0.75rem;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
    white-space: nowrap;
  }
  .flow-summary__meta {
    font-size: 0.68rem;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
    white-space: nowrap;
  }
</style>
